/**
 * Background info on composable here:
 * https://bioraft.atlassian.net/wiki/spaces/DEVEL/pages/3681517577/usePermissions
 */
import { ref, computed, set } from "vue";
import { restClient } from "@/lib/raftApi";
import { debounce } from "lodash";
import { useNotification } from "./useNotification";

const { showNotification } = useNotification();

// Store here so it is shared by all components.
const storedPermissions = ref({});
let queriesQueue = []; // array of objects with keys 'id' and 'queries'
const promises = ref({}); // store of promises, indexed by id
let count = 0; // used for ids

function updateStoredPermissions(responseData, queueItem) {
  return Object.keys(responseData.permissions).forEach((permission) => {
    const key = `${permission}:${queueItem["rog-id"] ?? "global"}`;
    if (storedPermissions?.value) {
      set(
        storedPermissions.value,
        key,
        Boolean(responseData.permissions[permission]),
      );
    }
  });
}

function makeRequest(queueItem) {
  const { queries } = queueItem;
  const path = "/perms";
  const adjustedQueries =
    queries["rog-id"] === "global"
      ? { permissions: queries.permissions }
      : queries;
  return restClient
    .get({ path, queries: adjustedQueries })
    .catch((e) => showNotification("error", e))
    .then((data) => updateStoredPermissions(data, queries));
}

function makeRequests(q) {
  while (q.length > 0) {
    const queueItem = q.pop();
    Promise.resolve(promises.value[queueItem.id](queueItem));
  }
}

const makeDebouncedRequests = debounce((q) => makeRequests(q), 50);

function createPromise(queries) {
  const newId = count;
  count += 1;
  queriesQueue.push({ id: newId, queries });
  // const index = queriesQueue.findIndex((i) => i.id === newId);
  promises.value[newId] = async (queueItem) => makeRequest(queueItem);
  return newId;
}

function amendOrCreatePromise(request) {
  // find a matching promise in the queue
  const matchIndex = queriesQueue.findIndex(
    ({ queries }) =>
      request["rog-id"] === queries["rog-id"] &&
      request["rog-type"] === queries["rog-type"],
  );
  if (matchIndex > -1) {
    request.permissions.forEach((perm) => {
      const queryPermissions = queriesQueue[matchIndex].queries.permissions;
      if (!queryPermissions) {
        queriesQueue[matchIndex].queries.permissions = [perm];
      } else if (!queryPermissions.includes(perm)) {
        queriesQueue[matchIndex].queries.permissions.push(perm);
      }
    });
    const id = queriesQueue[matchIndex].id;
    return id;
  }
  // if none, create and add to queue; return id
  return createPromise(request);
}

function checkForGroupContext(unprocessedGroupContext) {
  return unprocessedGroupContext ? String(unprocessedGroupContext) : "global";
}

// eslint-disable-next-line import/prefer-default-export
export function usePermissions(rawGroupContext) {
  // Handle null and false group contexts as well as numbers.
  const groupContext = checkForGroupContext(rawGroupContext);

  // Resets stored ref values for Cypress testing
  function resetStoreForTesting() {
    storedPermissions.value = {};
    queriesQueue = [];
  }

  function createPromisesAndMakeRequest(missingPerms, permMap) {
    let queries = {};
    if (groupContext !== "") {
      queries = { "rog-id": groupContext, "rog-type": "node" };
    }
    queries.permissions = missingPerms;
    const id = amendOrCreatePromise(queries);
    makeDebouncedRequests(queriesQueue);
    queries.permissions.forEach((perm) => {
      permMap.set(perm, promises.value[id]);
    });
  }

  function findMissingPermissions(permsToCheck, permMap, missingPerms) {
    permsToCheck.forEach((permission) => {
      const key = `${permission}:${groupContext}`;
      const hasStoredPermission = storedPermissions?.value?.hasOwnProperty(key);
      if (hasStoredPermission) {
        permMap.set(
          permission,
          storedPermissions.value ? storedPermissions.value[key] : false,
        );
      } else {
        missingPerms.push(permission);
      }
    });
  }

  // Fetches for permissions, avoiding duplicate requests
  async function getPermissions(desiredPermissions) {
    const missingPermissions = [];
    const ret = new Map();
    findMissingPermissions(desiredPermissions, ret, missingPermissions);
    if (missingPermissions.length > 0) {
      createPromisesAndMakeRequest(missingPermissions, ret);
    }
    return ret;
  }

  function hasPermissions(questions) {
    return questions.every(
      (perm) => storedPermissions?.value?.[`${perm}:${groupContext}`] === true,
    );
  }

  function filterAndMapPermissions(group) {
    if (!storedPermissions.value) return {};
    const filtered = Object.entries(storedPermissions.value).filter(([key]) =>
      key.includes(group),
    );
    const ret = {};
    filtered.forEach(([key, value]) => {
      ret[key.replace(`:${group}`, "")] = value;
    });
    return ret;
  }

  const permsInContext = computed(() => filterAndMapPermissions(groupContext));

  return {
    getPermissions,
    hasPermissions,
    permsInContext,
    storedPermissions,
    resetStoreForTesting,
  };
}
